import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import SmartImage from './SmartImage';
import ShareDraftModal from './common/ShareDraftModal';
import ShippingContactModal from './common/ShippingContactModal';
import { formatCurrency } from '../utils/formatters';
import { useToast } from '../contexts/ToastContext';

interface DraftDetailData {
  draft: {
    draft_id: string;
    draft_number: string;
    status: string;
    payment_status?: string;
    subtotal: number;
    shipping_cost: number;
    tax_amount: number;
    total_amount: number;
    notes?: string;
    reference?: string;
    created_at: string;
    expires_at?: string;
  };
  items: Array<{
    id: string;
    product: {
      id: number;
      sku: string;
      name: string;
      image_url?: string;
    };
    quantity: number;
    unit_price: number;
    total_price: number;
    is_bundled_item?: boolean;
    bundled_with_product_id?: number;
  }>;
  timeline: Array<{
    event: string;
    timestamp: string;
    user?: string;
    details?: any;
  }>;
  share_links: Array<{
    token: string;
    expires_at: string;
    created_at: string;
    access_count: number;
  }>;
}

const DraftDetail: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [draft, setDraft] = useState<DraftDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [timelineCollapsed, setTimelineCollapsed] = useState(true);
  const [shareHistoryCollapsed, setShareHistoryCollapsed] = useState(true);
  const [qrEnlarged, setQrEnlarged] = useState(false);
  const isDesktop = windowWidth >= 992;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchDraftDetails = async () => {
      try {
        const response = await api.get<DraftDetailData>(`/drafts/${quoteId}`);

        // Sort items: price descending, but bundled items follow their parent
        const items = response.data.items || [];

        // Separate bundled items from main items
        const mainItems = items.filter((item: any) => !item.is_bundled_item);
        const bundledItems = items.filter((item: any) => item.is_bundled_item);

        // Sort main items by price descending
        const sortedMainItems = mainItems.sort((a, b) => b.unit_price - a.unit_price);

        // Insert bundled items after their parent
        const sortedItems: any[] = [];
        sortedMainItems.forEach((item: any) => {
          sortedItems.push(item);
          // Find bundled items for this parent
          const itemBundled = bundledItems.filter((bundled: any) =>
            bundled.bundled_with_product_id === item.product.id
          );
          sortedItems.push(...itemBundled);
        });

        const sortedData = {
          ...response.data,
          items: sortedItems
        };
        setDraft(sortedData);
        setError('');
      } catch (err: any) {
        setError('Failed to load draft details');
        console.error('Draft fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (quoteId) {
      fetchDraftDetails();
    }
  }, [quoteId]);

  const handleShare = async () => {
    // Show contact modal first to collect shipping contact info
    setShowContactModal(true);
  };

  const handleContactSaved = async () => {
    // Close contact modal
    setShowContactModal(false);

    // Now create the share link
    try {
      const response = await api.post(`/drafts/${quoteId}/share`, {
        expires_in_hours: 168 // 7 days
      });
      // Use full URL with hostname
      const fullUrl = response.data.share_url.startsWith('http')
        ? response.data.share_url
        : `${window.location.origin}${response.data.share_url}`;

      console.log('DraftDetail Share - API response:', response.data.share_url);
      console.log('DraftDetail Share - Full URL:', fullUrl);

      setShareLink(fullUrl);
      setShowShareModal(true);

      // Auto-copy to clipboard
      await navigator.clipboard.writeText(fullUrl);
    } catch (error) {
      console.error('Failed to create share link:', error);
      showError('Failed to create share link');
    }
  };

  const handleDelete = async () => {
    if (!draft) return;

    const draftNumber = formatDraftNumber(draft.draft.draft_number);
    if (!window.confirm(`Are you sure you want to delete draft ${draftNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/drafts/${quoteId}`);
      showSuccess('Draft deleted successfully');
      navigate('/drafts');
    } catch (err: any) {
      console.error('Failed to delete draft:', err);
      showError('Failed to delete draft');
    }
  };


  // Format draft number from Q-2025-0911-0001 to DFT-0911-0001
  const formatDraftNumber = (quoteNumber: string) => {
    if (!quoteNumber) return '';
    const parts = quoteNumber.split('-');
    if (parts.length >= 4) {
      return `DFT-${parts[2]}-${parts[3]}`;
    }
    return `DFT-${quoteNumber.slice(-8)}`;
  };
  
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    const statusClasses: { [key: string]: string } = {
      'quote': 'bg-primary',
      'draft': 'bg-primary',
      'pending_approval': 'bg-warning',
      'approved': 'bg-success',
      'rejected': 'bg-danger',
      'expired': 'bg-secondary',
      'converted': 'bg-info'
    };
    
    const statusLabels: { [key: string]: string } = {
      'quote': 'DRAFT',
      'pending_approval': 'PENDING APPROVAL',
      'approved': 'APPROVED',
      'rejected': 'REJECTED',
      'expired': 'EXPIRED',
      'converted': 'CONVERTED'
    };
    
    return (
      <span className={`badge ${statusClasses[statusLower] || 'bg-secondary'} fs-6`}>
        {statusLabels[statusLower] || status.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading draft...</span>
            </div>
            <p className="mt-3">Loading draft details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !draft) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error || 'Draft not found'}
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/drafts')}
          >
            <i className="fas fa-arrow-left me-2"></i>Back to Drafts
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px 15px'
      }}>
        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h1 className="h2 mb-2">
                Draft {formatDraftNumber(draft.draft.draft_number)}
              </h1>
              <p className="text-muted mb-0">Created on {formatDate(draft.draft.created_at)}</p>
              {draft.draft.expires_at && (
                <p className="text-muted">Expires on {formatDate(draft.draft.expires_at)}</p>
              )}
              <div className="d-flex gap-2 align-items-center mt-2">
                {getStatusBadge(draft.draft.status)}
              </div>
            </div>
            
            <div style={{
              marginTop: isDesktop ? '0' : '20px',
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              justifyContent: isDesktop ? 'flex-end' : 'flex-start'
            }}>
              {(draft.draft.status === 'quote' || draft.draft.status === 'QUOTE' || draft.draft.status === 'DRAFT') && (
                <>
                  <button
                    className="btn"
                    onClick={handleShare}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    Share
                  </button>
                  <button
                    className="btn"
                    onClick={() => navigate(`/checkout/draft/${quoteId}`)}
                    style={{
                      backgroundColor: '#2E7D32',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    Checkout
                  </button>
                  <button
                    onClick={handleDelete}
                    title="Delete Draft"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      fontSize: '20px'
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Layout with Responsive Columns */}
        <div style={{
          display: 'flex',
          flexWrap: isDesktop ? 'nowrap' : 'wrap',
          gap: '24px'
        }}>
          {/* Left Column - Items and Timeline */}
          <div style={{
            flex: isDesktop ? '0 0 66.66667%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(66.66667% - 12px)' : '100%'
          }}>
            {/* Draft Items Card */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-box me-2"></i>Draft Items ({draft.items.length})
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <table className="table mb-0">
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                      <tr>
                        <th>Product</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.items.map(item => (
                        <tr key={item.id}>
                          <td style={{ verticalAlign: 'middle' }}>
                            <div className="d-flex align-items-center">
                              <div className="me-3" style={{
                                width: '60px',
                                height: '60px',
                                minWidth: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                flexShrink: 0
                              }}>
                                <SmartImage
                                  src={item.product.image_url || `/images/products/${item.product.id}`}
                                  alt={item.product.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                  productName={item.product.name}
                                  productSku={item.product.sku}
                                />
                              </div>
                              <div style={{ minHeight: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div className="fw-bold"
                                     title={item.product.name}
                                     style={{
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                       whiteSpace: 'nowrap',
                                       maxWidth: '350px'
                                     }}>
                                  {item.product.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>{item.quantity}</td>
                          <td className="text-end" style={{ verticalAlign: 'middle' }}>{formatCurrency(item.unit_price)}</td>
                          <td className="text-end fw-bold" style={{ verticalAlign: 'middle' }}>{formatCurrency(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Activity Timeline - Collapsible */}
            {draft.timeline.length > 0 && (
              <div className="card shadow-sm">
                <div className="card-header bg-light" 
                     style={{ cursor: 'pointer' }}
                     onClick={() => setTimelineCollapsed(!timelineCollapsed)}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-history me-2"></i>Activity Timeline
                    </h5>
                    <i className={`fas fa-chevron-${timelineCollapsed ? 'down' : 'up'}`}></i>
                  </div>
                </div>
                {!timelineCollapsed && (
                  <div className="card-body">
                    <div className="timeline">
                      {draft.timeline.map((event, index) => (
                        <div key={index} className="d-flex mb-3">
                          <div className="flex-shrink-0">
                            <div className="bg-primary rounded-circle p-2 text-white" 
                                 style={{width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <i className="fas fa-clock" style={{fontSize: '14px'}}></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1">{event.event}</h6>
                            <p className="text-muted mb-0">{formatDate(event.timestamp)}</p>
                            {event.user && <small className="text-muted">by {event.user}</small>}
                            {event.details?.notes && (
                              <p className="mt-2 mb-0"><em>{event.details.notes}</em></p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Summary and Details */}
          <div style={{
            flex: isDesktop ? '0 0 33.33333%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(33.33333% - 12px)' : '100%'
          }}>
            {/* Draft Summary Card */}
            <div className="card shadow-sm mb-4">
              <div className="card-header" style={{
                backgroundColor: '#4D64C1',
                color: 'white'
              }}>
                <h5 className="mb-0">Draft Summary</h5>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(draft.draft.subtotal)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Shipping:</span>
                  <span>{formatCurrency(draft.draft.shipping_cost)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Tax:</span>
                  <span>{formatCurrency(draft.draft.tax_amount)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <strong>Total:</strong>
                  <strong className="text-primary">{formatCurrency(draft.draft.total_amount)}</strong>
                </div>
              </div>
            </div>

            {draft.draft.notes && (
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <i className="fas fa-sticky-note me-2"></i>Notes
                  </h6>
                </div>
                <div className="card-body">
                  <p className="mb-0">{draft.draft.notes}</p>
                </div>
              </div>
            )}

            {draft.share_links.length > 0 && (
              <div className="card shadow-sm">
                <div
                  className="card-header bg-light"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShareHistoryCollapsed(!shareHistoryCollapsed)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <i className="fas fa-share-alt me-2"></i>Share History
                    </h6>
                    <i className={`fas fa-chevron-${shareHistoryCollapsed ? 'down' : 'up'}`}></i>
                  </div>
                </div>
                {!shareHistoryCollapsed && (
                  <div className="card-body">
                    {draft.share_links.map((link, index) => (
                      <div key={index} className="mb-2">
                        <small className="text-muted d-block">
                          Shared on {(() => {
                            const date = new Date(link.created_at);
                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                            const day = date.getDate().toString().padStart(2, '0');
                            const year = date.getFullYear();
                            return `${month}/${day}/${year}`;
                          })()}
                        </small>
                        <small className="text-muted">
                          Viewed {link.access_count} time{link.access_count !== 1 ? 's' : ''}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {draft && (
        <ShippingContactModal
          show={showContactModal}
          onClose={() => setShowContactModal(false)}
          onSuccess={handleContactSaved}
          draftId={draft.draft.draft_id}
          draftNumber={`DFT-${draft.draft.draft_number.slice(-8)}`}
        />
      )}

      <ShareDraftModal
        show={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setQrEnlarged(false);
        }}
        shareLink={shareLink}
        draftNumber={draft ? `DFT-${draft.draft.draft_number.slice(-8)}` : undefined}
      />
    </Layout>
  );
};

export default DraftDetail;